import type { HeadersFunction, LoaderFunction, MetaFunction } from '@remix-run/node';
import { useLoaderData, useRouteLoaderData } from '@remix-run/react';

import StaticPage from '~/components/StaticPage';
import { staticPageClientLoader } from '~/components/StaticPage/staticPageClientLoader';
import type { Author as AuthorData } from '~/interfaces/author';
import type { RootLoaderData } from '~/root';
import { findTipTapContentNode } from '~/utils/findTipTapContentNode';
import getAuthorPageMeta from '~/utils/getAuthorPageMeta';
import getStaticPageMetaData from '~/utils/getStaticPageMetaData';
import { siteFromMatches } from '~/utils/publicationFromLoaderData';
import { BASE_CACHE_TAG } from '~/utils/setCacheHeaders/index.server';
import { shouldRevalidateLoaderData } from '~/utils/shouldRevalidateLoaderData';

import { type StaticPageLoaderData, staticPageLoader } from '../../services/static_page';

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  return {
    'cache-control': loaderHeaders.get('cache-control') || 'max-age=0', // no-cache
    'Cache-Tag': loaderHeaders.get('Cache-Tag') || BASE_CACHE_TAG,
  };
};

const getFirstAuthor = (pageData?: Partial<StaticPageLoaderData>): AuthorData | null => {
  const content = pageData?.page?.viewable_page_version?.content;
  if (!content) return null;

  const authorNode = findTipTapContentNode(content, 'authors');
  const authors = (authorNode as { attrs?: { data?: { authors?: AuthorData[] } } } | undefined)?.attrs?.data?.authors;

  if (!Array.isArray(authors) || authors.length === 0) return null;

  return authors[0] || null;
};

export const meta: MetaFunction<typeof loader> = (args) => {
  const { data } = args;

  const author = getFirstAuthor(data);

  if (!author) {
    return getStaticPageMetaData(args);
  }

  const site = siteFromMatches(args.matches);
  const noindexAuthorIds = site?.viewable_site_version?.metadata?.noindex_author_ids ?? [];

  return getStaticPageMetaData(args, {
    ...getAuthorPageMeta(author, site?.publication),
    noindex: noindexAuthorIds.includes(author.id),
  });
};

export const loader: LoaderFunction = async ({ request, params, context }) => {
  const { authorId } = params;

  return staticPageLoader({
    request,
    context,
    pageRequestParams: {
      pathname: 'authors/$author',
      authorPageId: authorId,
    },
  });
};

export const clientLoader = staticPageClientLoader;

const Author = () => {
  const { page } = useLoaderData<typeof loader>();
  const { site } = useRouteLoaderData('root') as RootLoaderData;

  return <StaticPage page={page} site={site} />;
};

export const shouldRevalidate = shouldRevalidateLoaderData;

export default Author;
